import { CONNECTOR_LEVELS } from '@avalara/bat-apps-shared/constants';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';

import type { Result } from '@avalara/types/index';
import type { RouteComponentProps } from 'react-router-dom';

import { basePostParams } from '.';
import { formatPrice } from '../../../shared/helpers';
import { getEsbConnectorUrl } from '../../components/helpers/redirect';
import conf from '../../conf';
import { getAnalyticsByActiveFlow } from '../../reducers/aggregate-logic';
import { getEsbConfig } from '../../selectors/esb';
import { receiveAccountInitializationDetails } from '../../store/avatax-signup';
import { setEsbConfigMap, setEsbConnectorReturnUrl, setEsbSignupKey } from '../../store/esb';
import { setOffer } from '../../store/offer';
import { selectPlan } from '../../store/order';
import { receivePreFilledSignupForm, setIsPrepopulated, toggleEmailVerification } from '../../store/shared-signup';
import { setSystemError } from '../../store/system';
import { setAccountSetupRetryKey } from '../actions';
import analytics, { scrubPostBody } from '../analytics';
import { formatAccountSetupError } from '../errors';
import fetch from '../fetch';
import { checkIsAuthenticated, openExternalLink } from '../helpers';
import { esbFormOneFields } from '../shared-signup';
import { formatObapiPostBody, verifyEmail } from './accounts';
import { updateProductAnalytics } from './analytics';
import { getSystemErrorMessaging } from './error';

import type { Dispatch } from '@avalara/types/actions';
import type { State } from '@avalara/types/reducer-states';
import type {
  AccountSetupDetails,
  BaseNewAccountForm,
  CreateObapiAccountRequest,
  SetUpAccountErrorDetails,
} from '../../../../types';
import type { ClientEsbConfig, ClientEsbConfigMap } from '../../../../types/esb';
import type { ClientPartnerConfig } from '../../../../types/handshake';

const createNewEsbAccount = (dispatch: Dispatch, state: State) => {
  const esbConfig = getEsbConfig(state);
  const postBody: CreateObapiAccountRequest = {
    ...formatObapiPostBody(state),
    esbPartner: state.esb.esbPartner,
    key: state.esb.esbSignupKey,
    isPaymentRequired: esbConfig?.isPaymentRequired,
  };
  const postParams = {
    ...basePostParams,
    body: JSON.stringify(postBody),
  };

  return fetch(dispatch)<AccountSetupDetails, SetUpAccountErrorDetails>(
    `${conf.API_URL}/api/accounts/offers/esb`,
    postParams,
  );
};

export const createEsbAccount =
  (history: RouteComponentProps['history']) => async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();

    const isEmailValid = await verifyEmail(state.sharedSignup.signupForm.email, dispatch);
    if (!isEmailValid) {
      dispatch(toggleEmailVerification(false));
      return;
    }

    const {
      esb: { connectorReturnUrl, esbPartner, esbPath },
    } = state;

    const result = await createNewEsbAccount(dispatch, getState());
    if (!result.ok) {
      const esbConnectorUrl = getEsbConnectorUrl({
        connectorReturnUrl,
        userExists: /already exists/.test(result.message),
        error: result.status === 400 || result.status === 403,
      });

      const { html, message, title, locationAfterErrorDismissal } = formatAccountSetupError({
        resultMessage: result.message,
        redirectUrl: esbConnectorUrl,
        productName: 'Avalara AvaTax',
      });

      if (message || html) {
        dispatch(
          setSystemError({
            html,
            message,
            title,
            secondaryMessage: [],
            locationAfterErrorDismissal,
            serverMessage: result.message,
          }),
        );
        return;
      }

      const newRetryKey = result.details?.retryKey;
      if (newRetryKey) dispatch(setAccountSetupRetryKey(newRetryKey));

      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: 'We were unable to set up your account.',
          serverMsg: result.message,
          isPaid: false,
        }),
      );
      return;
    }

    dispatch(receiveAccountInitializationDetails(result.value));

    dispatch(updateProductAnalytics('purchase'));

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);
    history.push(`/${esbPath}/${esbPartner || ''}/sign-in`);
  };

export const isEsbCustomerDataComplete = (fields: Partial<BaseNewAccountForm>): boolean =>
  esbFormOneFields.every((requiredField) => !!fields[requiredField as keyof typeof fields]?.trim());

const checkIfEsbSignupIsAuthorized = async (
  esbPartner: string,
  key: string | null,
  dispatch: Dispatch,
): Promise<Result<Partial<BaseNewAccountForm>>> =>
  fetch(dispatch)(`${conf.API_URL}/api/handshake/esb/${esbPartner}`, {}, { Authorization: key ?? '' });

export const getEsbConfigMap = (partnerId: string) => async (dispatch: Dispatch) => {
  const result = await fetch(dispatch)<ClientEsbConfigMap>(`${conf.API_URL}/api/esb/${partnerId}`);
  const esbConfigMap = result.ok ? result.value : {};
  dispatch(setEsbConfigMap(esbConfigMap));
};

// NOTE (Andrew) - we need to additionally check for these values in session storage, since the
//                 user can log in via the header menu, which will not preserve these queries on
//                 return back to buydot.
export const getConnectorReturnUrl = (esbConfig: ClientEsbConfig | Partial<ClientPartnerConfig>): string => {
  const searchParams = new URLSearchParams(window.location.search);
  let connectorReturnUrl = searchParams.get('returnUrl');

  if (connectorReturnUrl == null) {
    connectorReturnUrl = sessionStorage.get('connectorReturnUrl') || null;
    if (connectorReturnUrl != null) sessionStorage.remove('connectorReturnUrl');
  } else {
    sessionStorage.set('connectorReturnUrl', connectorReturnUrl);
  }

  if (connectorReturnUrl != null) return connectorReturnUrl;
  return esbConfig?.returnUrl ?? '';
};

export const addEsbOffer = async (dispatch: Dispatch, state: State): Promise<void> => {
  const {
    account: { selectedPaymentMethod },
    data: { marketingData },
    esb: { connectorReturnUrl, esbPartner, esbSignupKey },
    system: { activeFlow },
  } = state;

  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get('id');

  const postBody = {
    activeFlow,
    esbPartner,
    // If called on page load, esbSignupKey may not be set yet, so default to
    // id from query params:
    key: esbSignupKey || id,
    marketingData,
    paymentMethodId: selectedPaymentMethod?.Id,
  };

  const postParams = {
    ...basePostParams,
    body: JSON.stringify(postBody),
  };

  const result = await fetch(dispatch, { showLoadingWheel: false })(`${conf.API_URL}/api/esb/add`, postParams);

  if (!result.ok) {
    analytics.track('PARTNER_ADD_ON_FAILURE', {
      message: result.message,
      status: result.status,
      body: scrubPostBody(postBody),
      error: true,
    });

    const esbConnectorUrl = getEsbConnectorUrl({
      connectorReturnUrl,
      userExists: true,
      error: result.status >= 400,
    });

    // TODO - refine error handling
    dispatch(
      getSystemErrorMessaging({
        errorCode: 'genericError',
        locationString: esbConnectorUrl,
      }),
    );
    return;
  }

  const esbConnectorUrl = getEsbConnectorUrl({ connectorReturnUrl, userExists: true });
  openExternalLink(esbConnectorUrl);
};

export const initializeEsbFlow =
  () =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const esbConfig = getEsbConfig(getState());
    if (!esbConfig) return;
    const returnUrl = getConnectorReturnUrl(esbConfig);
    dispatch(setEsbConnectorReturnUrl(returnUrl));
    // NOTE (Andrew) - the following destructuring of connectorReturnUrl from state needs to happen
    //                 _after_ we dispatch the setting of the return url from "getConnectorReturnUrl"
    //                 on the line above, which is where we set it in our state.
    const state = getState();
    const {
      sharedSignup: { signupForm },
      esb: { connectorReturnUrl, esbSignupKey },
      account: { isLoggedIn },
    } = state;

    if (isLoggedIn) {
      // If add on is not enabled, send them back to the returnUrl with ?userExists=true
      if (!esbConfig.isAddOnEnabled) {
        const esbConnectorUrl = getEsbConnectorUrl({
          connectorReturnUrl,
          userExists: true,
        });

        openExternalLink(esbConnectorUrl);
        return;
      }
      // TODO - handle the scenario where add on is enabled *AND* payment is required
      // if (!esbConfig.isPaymentRequired) {
      await addEsbOffer(dispatch, state);
      return;
      // }
    }

    // If the user is authenticated, but their account fetch has not resolved yet,
    // then skip the parts of this function that are relevant only to net-new users:
    if (checkIsAuthenticated()) return;

    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    let customerData = signupForm;

    /* NOTE(Sasha): If a user navigates back to the initial ESB start page, we
     should not re-check for the authorization key. Otherwise it may send users
     to the AI login. This is why we skip if `esbSignupKey` is already set.
     Otherwise, only check if it's either a private offer, in which case we
     need to validate that their key is valid, or if an id query is passed,
     indicating that there may be data saved with which to pre-populate the
     signup form.
  */
    if (!esbSignupKey && (esbConfig.isPrivateOffer || !!id)) {
      // checkIfEsbSignupIsAuthorized also will return any cached data
      // associated with the specified id for pre-populating the signup form
      const authorizationRes = await checkIfEsbSignupIsAuthorized(esbConfig.id, id, dispatch);

      if (!authorizationRes.ok) {
        // If the returnUrl has not been specified, redirect to Buy home
        if (connectorReturnUrl === '') {
          window.location.assign('/');
          return;
        }
        const esbConnectorUrl = getEsbConnectorUrl({
          connectorReturnUrl,
          error: true,
        });
        openExternalLink(esbConnectorUrl);
        return;
      }

      customerData = { ...customerData, ...authorizationRes.value };
    }

    if (id != null) {
      dispatch(setEsbSignupKey(id));
    }

    if (isEsbCustomerDataComplete(customerData)) dispatch(setIsPrepopulated(true));

    dispatch(receivePreFilledSignupForm(customerData));
  };

export const setEsbBackground = (elementId: string) => () => {
  const esbContainer = document.getElementById(elementId);
  if (esbContainer != null) {
    esbContainer.style.backgroundImage = `url(${conf.ASSET_PATH}/assets/images/pottery-background.jpg)`;
  }
};

export const initializeBigCommercePaidOffer = () => async (dispatch: Dispatch) => {
  const { overagePrice, recurringPrice } = CONNECTOR_LEVELS['Level 2'].paidOffer2;

  dispatch(
    setOffer({
      analyticsProductCategory: '',
      attributionDefaults: {},
      isPaymentRequired: true,
      offerFlow: null,
      offerName: '',
      orderSummary: {
        planIncludes: {
          heading: 'Your Avalara AvaTax annual subscription includes:',
          items: [
            'Up to 500 transactions per year',
            'Automated sales tax calculation',
            'Automated tax code classification',
            'Economic nexus tracking with our heat map',
          ],
        },
        totalText: 'Total due today',
        showBillingTermsInOrderSummary: true,
      },
      pages: [],
      plans: [
        {
          name: 'bigcommerce',
          planSpecificBillingTerms: [`An overage rate of ${formatPrice(overagePrice)} per transaction may apply.`],
          productItems: [
            {
              name: 'Avalara AvaTax annual subscription fee',
              price: recurringPrice,
            },
          ],
        },
      ],
      taxCode: 'SW052000',
      thankYouContent: {},
    }),
  );

  dispatch(selectPlan({ planName: 'bigcommerce', planSelectComplete: true }));
};
